import { AUTO_RECOMMENDED_ALERTS_ENDPOINT } from '../constants/EndPoints';
import { fetchMiddleware } from './MiddlewareActions';
import { selectFeature } from './selectors';
import { selectAlert } from './AlertsActions';
import { changeAlertsContext } from './PrsTransientAlertsActions';
import { browserHistory } from 'react-router';

// Action Types
export const SET_AUTO_RECOMMENDED_ALERTS_MODAL_VISIBILITY = 'SET_AUTO_RECOMMENDED_ALERTS_MODAL_VISIBILITY';

export const FETCH_AUTO_RECOMMENDED_ALERTS_REQUEST = 'FETCH_AUTO_RECOMMENDED_ALERTS_REQUEST';
export const FETCH_AUTO_RECOMMENDED_ALERTS_SUCCESS = 'FETCH_AUTO_RECOMMENDED_ALERTS_SUCCESS';
export const FETCH_AUTO_RECOMMENDED_ALERTS_FAILURE = 'FETCH_AUTO_RECOMMENDED_ALERTS_FAILURE';

export const CHANGE_AUTO_ALERT_STATE_REQUEST = 'CHANGE_AUTO_ALERT_STATE_REQUEST';
export const CHANGE_AUTO_ALERT_STATE_SUCCESS = 'CHANGE_AUTO_ALERT_STATE_SUCCESS';
export const CHANGE_AUTO_ALERT_STATE_FAILURE = 'CHANGE_AUTO_ALERT_STATE_FAILURE';

export const CHANGE_AUTO_ALERT_PRIORITY_REQUEST = 'CHANGE_AUTO_ALERT_PRIORITY_REQUEST';
export const CHANGE_AUTO_ALERT_PRIORITY_SUCCESS = 'CHANGE_AUTO_ALERT_PRIORITY_SUCCESS';
export const CHANGE_AUTO_ALERT_PRIORITY_FAILURE = 'CHANGE_AUTO_ALERT_PRIORITY_FAILURE';

export const ADD_AUTO_ALERT_FEEDBACK_REQUEST = 'ADD_AUTO_ALERT_FEEDBACK_REQUEST';
export const ADD_AUTO_ALERT_FEEDBACK_SUCCESS = 'ADD_AUTO_ALERT_FEEDBACK_SUCCESS';
export const ADD_AUTO_ALERT_FEEDBACK_FAILURE = 'ADD_AUTO_ALERT_FEEDBACK_FAILURE';

export const FETCH_AUTO_ALERT_HISTORY_REQUEST = 'FETCH_AUTO_ALERT_HISTORY_REQUEST';
export const FETCH_AUTO_ALERT_HISTORY_SUCCESS = 'FETCH_AUTO_ALERT_HISTORY_SUCCESS';
export const FETCH_AUTO_ALERT_HISTORY_FAILURE = 'FETCH_AUTO_ALERT_HISTORY_FAILURE';

const JSON_HEADERS = { 'Accept': 'application/json', 'Content-Type': 'application/json' };

const errMsg = (response, fallback) =>
  response.err?.message || response.err || response.data || fallback;

// Action Creators
export const setAutoRecommendedAlertsModalVisibility = (isVisible) => ({
  type: SET_AUTO_RECOMMENDED_ALERTS_MODAL_VISIBILITY,
  payload: isVisible
});

// Thunks
export const fetchAutoRecommendedAlerts = (projectId) => {
  return async (dispatch, getState) => {
    dispatch({ type: FETCH_AUTO_RECOMMENDED_ALERTS_REQUEST });
    try {
      const path = `${AUTO_RECOMMENDED_ALERTS_ENDPOINT}/${projectId}`;
      const options = { method: 'GET', headers: JSON_HEADERS };
      const response = await fetchMiddleware(path, options, getState);
      if (response.status) {
        dispatch({ type: FETCH_AUTO_RECOMMENDED_ALERTS_SUCCESS, payload: response.data });
      } else {
        dispatch({ type: FETCH_AUTO_RECOMMENDED_ALERTS_FAILURE, error: errMsg(response, 'Failed to fetch auto recommended alerts') });
      }
      return response;
    } catch (error) {
      dispatch({ type: FETCH_AUTO_RECOMMENDED_ALERTS_FAILURE, error: error.message });
      throw error;
    }
  };
};

export const changeAutoAlertState = (alertId, newState, feedback) => {
  return async (dispatch, getState) => {
    dispatch({ type: CHANGE_AUTO_ALERT_STATE_REQUEST });
    try {
      const path = `${AUTO_RECOMMENDED_ALERTS_ENDPOINT}/${alertId}/state`;
      const options = {
        method: 'PUT',
        headers: JSON_HEADERS,
        // Backend column is user_feedback_text (feedback is only a read-alias, not a
        // real column). Send only the real column name to avoid an invalid-column error.
        body: JSON.stringify({ event_state: parseInt(newState), user_feedback_text: feedback })
      };
      const response = await fetchMiddleware(path, options, getState);
      if (response.status) {
        dispatch({ type: CHANGE_AUTO_ALERT_STATE_SUCCESS, payload: { alertId, newState, feedback } });
        const projectId = getState().leaksList?.selectedProject;
        if (projectId) dispatch(fetchAutoRecommendedAlerts(projectId));
      } else {
        dispatch({ type: CHANGE_AUTO_ALERT_STATE_FAILURE, error: errMsg(response, 'Failed to change alert state') });
      }
      return response;
    } catch (error) {
      dispatch({ type: CHANGE_AUTO_ALERT_STATE_FAILURE, error: error.message });
      throw error;
    }
  };
};

export const changeAutoAlertPriority = (alertId, newPriority) => {
  return async (dispatch, getState) => {
    dispatch({ type: CHANGE_AUTO_ALERT_PRIORITY_REQUEST });
    try {
      const path = `${AUTO_RECOMMENDED_ALERTS_ENDPOINT}/${alertId}/priority`;
      const options = {
        method: 'PUT',
        headers: JSON_HEADERS,
        body: JSON.stringify({ event_priority: parseInt(newPriority) })
      };
      const response = await fetchMiddleware(path, options, getState);
      if (response.status) {
        dispatch({ type: CHANGE_AUTO_ALERT_PRIORITY_SUCCESS, payload: { alertId, newPriority } });
        const projectId = getState().leaksList?.selectedProject;
        if (projectId) dispatch(fetchAutoRecommendedAlerts(projectId));
      } else {
        dispatch({ type: CHANGE_AUTO_ALERT_PRIORITY_FAILURE, error: errMsg(response, 'Failed to change alert priority') });
      }
      return response;
    } catch (error) {
      dispatch({ type: CHANGE_AUTO_ALERT_PRIORITY_FAILURE, error: error.message });
      throw error;
    }
  };
};

export const addAutoAlertFeedback = (alertId, feedback) => {
  return async (dispatch, getState) => {
    dispatch({ type: ADD_AUTO_ALERT_FEEDBACK_REQUEST });
    try {
      const path = `${AUTO_RECOMMENDED_ALERTS_ENDPOINT}/${alertId}/feedback`;
      // Backend column is user_feedback_text (feedback is only a read-alias, not a real column).
      const options = { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ user_feedback_text: feedback }) };
      const response = await fetchMiddleware(path, options, getState);
      if (response.status) {
        dispatch({ type: ADD_AUTO_ALERT_FEEDBACK_SUCCESS, payload: { alertId, feedback } });
        const projectId = getState().leaksList?.selectedProject;
        if (projectId) dispatch(fetchAutoRecommendedAlerts(projectId));
      } else {
        dispatch({ type: ADD_AUTO_ALERT_FEEDBACK_FAILURE, error: errMsg(response, 'Failed to add feedback') });
      }
      return response;
    } catch (error) {
      dispatch({ type: ADD_AUTO_ALERT_FEEDBACK_FAILURE, error: error.message });
      throw error;
    }
  };
};

export const fetchAutoAlertHistory = (alertId) => {
  return async (dispatch, getState) => {
    dispatch({ type: FETCH_AUTO_ALERT_HISTORY_REQUEST });
    try {
      const path = `${AUTO_RECOMMENDED_ALERTS_ENDPOINT}/${alertId}/history`;
      const options = { method: 'GET', headers: JSON_HEADERS };
      const response = await fetchMiddleware(path, options, getState);
      if (response.status) {
        dispatch({ type: FETCH_AUTO_ALERT_HISTORY_SUCCESS, payload: response.data });
      } else {
        dispatch({ type: FETCH_AUTO_ALERT_HISTORY_FAILURE, error: errMsg(response, 'Failed to fetch alert history') });
      }
      return response;
    } catch (error) {
      dispatch({ type: FETCH_AUTO_ALERT_HISTORY_FAILURE, error: error.message });
      throw error;
    }
  };
};

// Selection + navigation: focus the matching leak on map/table/inspect.
export const selectAutoRecommendedAlert = (alertId) => {
  return (dispatch, getState) => {
    const state = getState();
    const selectedProject = state.leaksList?.selectedProject;
    const leaks = state.leaksList?.leaksByProject?.[selectedProject]?.items || [];
    const leak = leaks.find(l => l.id === alertId || l.ID === alertId);
    if (selectedProject && alertId) {
      // Ensure the map/table is on the leak-alerts layer, not couples/noise/valve,
      // otherwise selecting the feature surfaces the wrong entity (e.g. a couple).
      const alertsContext = state.leaksList?.pressure?.context;
      if (alertsContext !== 'alerts') {
        dispatch(changeAlertsContext('alerts'));
      }
      if (leak) {
        dispatch(selectFeature(selectedProject, leak.ID || leak.id, 'leak', leak));
        dispatch(selectAlert(leak));
      }
      browserHistory.push('/alerts');
      // When the alert is filtered out of the list, Page's focusAlert handler
      // shows the "hidden by filters" notification with a DISPLAY ALERT action.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('focusAlert', {
          detail: { alertId: leak ? (leak.ID || leak.id) : alertId, keepDashboardOpen: true }
        }));
      }, 1000);
    }
  };
};
